import React, { useCallback, useEffect, useState } from 'react';
import {
  X,
  Network,
  BookOpen,
  Workflow,
  Database,
  Link2,
  Copy,
  Check,
  ExternalLink,
  ArrowUpRight,
  Users,
  Shield,
  Activity,
  MessageSquare,
  Zap,
  Server,
  Clock,
  TrendingUp,
  AlertTriangle,
  Building2,
  UserCheck,
  LayoutDashboard,
  Radio,
  UserX,
  KeyRound,
  RefreshCw,
  ArrowLeft,
} from 'lucide-react';
import {
  AccessExclusion,
  EnterpriseChatBotResourceMap,
  HealthMetrics,
  ResourceMapIntent,
  ResourceMapKnowledgeBase,
  ResourceMapLinkedResource,
  categoryColor,
  fetchChatBotResourceMap,
  formatConversations24h,
  formatNullable,
  formatResourceSubtitle,
  isActiveIntent,
  knowledgeBaseHref,
  resourceHref,
} from './copilotResourceMapService';

export interface CopilotResourceMapAgent {
  id: string;
  agent_id: string;
  agent_title: string;
  agent_icon?: string;
}

type TabId = 'overview' | 'resources' | 'intents' | 'access' | 'observability';

interface CopilotResourceMapModalProps {
  agent: CopilotResourceMapAgent;
  onClose: () => void;
}

interface AccessListItem {
  id: string;
  name: string;
  detail?: string;
}

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <LayoutDashboard className="w-4 h-4" /> },
  { id: 'resources', label: 'Resources', icon: <BookOpen className="w-4 h-4" /> },
  { id: 'intents', label: 'Intents', icon: <Zap className="w-4 h-4" /> },
  { id: 'access', label: 'Access Management', icon: <KeyRound className="w-4 h-4" /> },
  { id: 'observability', label: 'Observability', icon: <Activity className="w-4 h-4" /> },
];

const CATEGORY_CHIP: Record<string, string> = {
  blue: 'text-blue-300 bg-blue-500/10 border-blue-500/25',
  purple: 'text-purple-300 bg-purple-500/10 border-purple-500/25',
  orange: 'text-orange-300 bg-orange-500/10 border-orange-500/25',
};

const CATEGORY_STRIPE: Record<string, string> = {
  blue: 'bg-blue-500',
  purple: 'bg-purple-500',
  orange: 'bg-orange-500',
};

const RESOURCE_TYPE_META: Record<
  string,
  { label: string; icon: React.ReactNode; listBg: string }
> = {
  knowledge_base: {
    label: 'Knowledge Base',
    icon: <BookOpen className="w-3.5 h-3.5" />,
    listBg: 'bg-emerald-500/[0.07] border-emerald-500/15',
  },
  orchestrator: {
    label: 'Orchestrator',
    icon: <Workflow className="w-3.5 h-3.5" />,
    listBg: 'bg-blue-500/[0.07] border-blue-500/15',
  },
  data_model: {
    label: 'Data Model',
    icon: <Database className="w-3.5 h-3.5" />,
    listBg: 'bg-amber-500/[0.07] border-amber-500/15',
  },
};

function getCategoryChip(category: string): string {
  const color = categoryColor[category] ?? 'blue';
  return CATEGORY_CHIP[color] ?? CATEGORY_CHIP.blue;
}

function getCategoryStripe(category: string): string {
  const color = categoryColor[category] ?? 'blue';
  return CATEGORY_STRIPE[color] ?? CATEGORY_STRIPE.blue;
}

function getResourceTypeMeta(resourceType: string) {
  return (
    RESOURCE_TYPE_META[resourceType] ?? {
      label: resourceType.replace(/_/g, ' '),
      icon: <Server className="w-3.5 h-3.5" />,
      listBg: 'bg-neutral-500/[0.07] border-neutral-500/15',
    }
  );
}

function formatHealthMetric(
  value: number | null | undefined,
  suffix = '',
  fallback = 'N/A',
): string {
  if (value == null) return fallback;
  return `${value}${suffix}`;
}

function hasIntentAccess(intent: ResourceMapIntent): boolean {
  return (
    intent.access.departments.length > 0 ||
    intent.access.users.length > 0 ||
    intent.access.exclusions.length > 0
  );
}

// ─── Shared UI ────────────────────────────────────────────────────────────────

function NewTabLink({
  href,
  children,
  className = '',
  title,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className} title={title}>
      {children}
    </a>
  );
}

function Panel({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <section className={`rounded-xl border border-neutral-800 bg-neutral-900 ${className}`}>
      {children}
    </section>
  );
}

function PanelHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-neutral-800">
      <div>
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  loading = false,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-500">{label}</p>
        <span className="text-indigo-400/70">{icon}</span>
      </div>
      {loading ? (
        <div className="h-8 w-16 rounded-md bg-neutral-800 animate-pulse" />
      ) : (
        <p className="text-2xl font-semibold text-white tabular-nums">{value}</p>
      )}
    </div>
  );
}

function SkeletonBlock({ className = '' }: { className?: string }) {
  return <div className={`rounded-md bg-neutral-800 animate-pulse ${className}`} />;
}

function LoadingSkeleton() {
  return (
    <div className="space-y-5">
      <Panel>
        <div className="px-5 py-4 border-b border-neutral-800">
          <SkeletonBlock className="h-4 w-32 mb-2" />
          <SkeletonBlock className="h-3 w-48" />
        </div>
        <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i}>
              <SkeletonBlock className="h-3 w-20 mb-2" />
              <SkeletonBlock className="h-4 w-28" />
            </div>
          ))}
        </div>
      </Panel>
      <Panel>
        <div className="px-5 py-4 border-b border-neutral-800">
          <SkeletonBlock className="h-4 w-24" />
        </div>
        <div className="p-5">
          <SkeletonBlock className="h-10 w-full" />
        </div>
      </Panel>
    </div>
  );
}

function KnowledgeBaseRow({ kb }: { kb: ResourceMapKnowledgeBase }) {
  return (
    <li className="group px-5 py-3.5 flex items-start justify-between gap-3 transition-colors hover:bg-indigo-500/[0.05]">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm text-white truncate">{kb.name}</p>
          <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-md bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
            {kb.document_count} docs
          </span>
          {kb.slug && (
            <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-md font-mono bg-neutral-800 text-gray-400 border border-neutral-700">
              {kb.slug}
            </span>
          )}
        </div>
        {kb.description && (
          <p className="text-xs text-gray-500 mt-0.5 truncate">{kb.description}</p>
        )}
      </div>
      <NewTabLink
        href={knowledgeBaseHref(kb.knowledge_workspace_id, kb.id)}
        className="shrink-0 text-gray-500 hover:text-indigo-400 transition-colors"
        title="Open knowledge base"
      >
        <ArrowUpRight className="w-4 h-4" />
      </NewTabLink>
    </li>
  );
}

function AccessList({
  icon,
  title,
  items,
  emptyText,
  variant = 'default',
}: {
  icon: React.ReactNode;
  title: string;
  items: AccessListItem[];
  emptyText: string;
  variant?: 'default' | 'exclusion';
}) {
  return (
    <Panel>
      <PanelHeader title={title} subtitle={`${items.length} assigned`} />
      {items.length === 0 ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">{emptyText}</p>
      ) : (
        <ul className="divide-y divide-neutral-800/80">
          {items.map((item) => (
            <li
              key={item.id}
              className="px-5 py-3 flex items-start gap-3 hover:bg-indigo-500/[0.04] transition-colors"
            >
              <span
                className={`mt-0.5 shrink-0 ${variant === 'exclusion' ? 'text-red-400/80' : 'text-indigo-400/80'}`}
              >
                {icon}
              </span>
              <div className="min-w-0">
                <p className="text-sm text-white">{item.name}</p>
                {item.detail && <p className="text-xs text-gray-500 mt-0.5">{item.detail}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function IntentCard({ intent }: { intent: ResourceMapIntent }) {
  const categoryChip = getCategoryChip(intent.category);
  const categoryStripe = getCategoryStripe(intent.category);
  const active = isActiveIntent(intent.status);
  const showAccess = hasIntentAccess(intent);

  return (
    <article className="rounded-xl border border-neutral-800 bg-neutral-900 overflow-hidden">
      <div className="flex items-stretch">
        <div className={`w-1 shrink-0 ${categoryStripe}`} />
        <div className="flex-1 px-5 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold text-white">
                {formatNullable(intent.name, 'Unnamed intent')}
              </h3>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  active
                    ? 'bg-green-500/10 text-green-400 border border-green-500/25'
                    : 'bg-neutral-700/50 text-gray-400 border border-neutral-600/40'
                }`}
              >
                {formatNullable(intent.status, 'Unknown')}
              </span>
            </div>
            {intent.code && (
              <p className="text-xs font-mono text-gray-500 mt-0.5">{intent.code}</p>
            )}
            {intent.intent_type_label && (
              <p className="text-xs text-gray-400 mt-1">{intent.intent_type_label}</p>
            )}
          </div>
          <span
            className={`shrink-0 text-[10px] font-medium px-2.5 py-1 rounded-md border ${categoryChip}`}
          >
            {intent.category}
          </span>
        </div>
      </div>

      <div className="border-t border-neutral-800 grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-neutral-800">
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Resources</p>
            <span className="text-[10px] text-gray-600">{intent.resources.length} linked</span>
          </div>
          {intent.resources.length === 0 ? (
            <p className="text-xs text-gray-600">No resources linked.</p>
          ) : (
            <ul className="space-y-2">
              {intent.resources.map((res, index) => (
                <IntentResourceItem key={res.id ?? `${res.name}-${index}`} resource={res} />
              ))}
            </ul>
          )}
        </div>

        <div className="p-5 bg-indigo-500/[0.03]">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-3.5 h-3.5 text-indigo-400" />
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
              Intent Access
            </p>
          </div>

          {!showAccess ? (
            <p className="text-xs text-gray-500">No access rules configured.</p>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-[10px] text-gray-600 mb-1.5 flex items-center gap-1">
                  <Building2 className="w-3 h-3" /> Departments
                </p>
                {intent.access.departments.length === 0 ? (
                  <p className="text-xs text-gray-600">None</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {intent.access.departments.map((d) => (
                      <span
                        key={d.id}
                        className="text-xs px-2 py-1 rounded-md text-indigo-200 bg-indigo-500/10 border border-indigo-500/20"
                      >
                        {d.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="text-[10px] text-gray-600 mb-1.5 flex items-center gap-1">
                  <Users className="w-3 h-3" /> Users
                </p>
                {intent.access.users.length === 0 ? (
                  <p className="text-xs text-gray-600">None</p>
                ) : (
                  <ul className="space-y-1.5">
                    {intent.access.users.map((u) => (
                      <li key={u.id} className="text-xs">
                        <span className="text-white">{u.name}</span>
                        <span className="text-gray-500 ml-1.5">{u.email}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {intent.access.exclusions.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-600 mb-1.5 flex items-center gap-1">
                    <UserX className="w-3 h-3" /> Exclusions
                  </p>
                  <ul className="space-y-1.5">
                    {intent.access.exclusions.map((e) => (
                      <li
                        key={e.id}
                        className="text-xs px-2 py-1.5 rounded-md bg-red-500/8 border border-red-500/15"
                      >
                        <span className="text-red-300">{e.name}</span>
                        <p className="text-gray-500 mt-0.5">
                          {[e.email, e.note].filter(Boolean).join(' · ')}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function IntentResourceItem({ resource }: { resource: ResourceMapLinkedResource }) {
  const meta = getResourceTypeMeta(resource.resource_type);
  const subtitle = formatResourceSubtitle(resource);

  return (
    <li>
      <NewTabLink
        href={resourceHref(resource)}
        className={`group flex items-center justify-between gap-3 rounded-lg border px-3.5 py-2.5 transition-colors hover:brightness-110 ${meta.listBg}`}
        title="Open in new tab"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm text-white truncate">{resource.name}</p>
            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-neutral-700 text-gray-400">
              {meta.icon}
              {meta.label}
            </span>
          </div>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
        </div>
        <ArrowUpRight className="w-3.5 h-3.5 shrink-0 text-gray-500 group-hover:text-white transition-colors" />
      </NewTabLink>
    </li>
  );
}

function SummaryCards({
  data,
  loading,
}: {
  data: EnterpriseChatBotResourceMap | null;
  loading: boolean;
}) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      <StatCard
        label="Active Intents"
        value={data?.summary.active_intents ?? 0}
        icon={<Zap className="w-4 h-4" />}
        loading={loading}
      />
      <StatCard
        label="Knowledge Bases"
        value={data?.summary.knowledge_bases ?? 0}
        icon={<BookOpen className="w-4 h-4" />}
        loading={loading}
      />
      <StatCard
        label="Access Grants"
        value={data?.summary.access_grants ?? 0}
        icon={<KeyRound className="w-4 h-4" />}
        loading={loading}
      />
      <StatCard
        label="Conversations (24h)"
        value={loading ? '—' : formatConversations24h(data?.summary.conversations_24h ?? null)}
        icon={<MessageSquare className="w-4 h-4" />}
        loading={loading}
      />
    </div>
  );
}

// ─── Tab panels ───────────────────────────────────────────────────────────────

function OverviewTab({
  data,
  copied,
  onCopyUrl,
}: {
  data: EnterpriseChatBotResourceMap;
  copied: boolean;
  onCopyUrl: () => void;
}) {
  const { copilot_details: details, health_metrics: health } = data;
  const createdAt = details.created_at
    ? new Date(details.created_at).toLocaleDateString()
    : '—';

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel className="lg:col-span-2">
          <PanelHeader title="Copilot Details" subtitle="High-level configuration" />
          <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-5 text-sm">
            {[
              { label: 'Agent Type', value: formatNullable(details.agent_type) },
              { label: 'Chat Type', value: formatNullable(details.chat_type) },
              { label: 'LLM Model', value: formatNullable(details.llm_model) },
              {
                label: 'Languages',
                value: details.languages.length > 0 ? details.languages.join(', ') : '—',
              },
              { label: 'Created', value: createdAt },
              { label: 'Intents', value: String(details.intents_total) },
            ].map((field) => (
              <div key={field.label}>
                <p className="text-xs text-gray-500">{field.label}</p>
                <p className="text-white mt-1 font-medium">{field.value}</p>
              </div>
            ))}
          </div>
        </Panel>

        <HealthPanel health={health} />
      </div>

      {data.copilot_url && (
        <Panel>
          <PanelHeader title="Copilot URL" subtitle="Public chat endpoint" />
          <div className="p-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="flex-1 min-w-0 rounded-lg border border-neutral-800 bg-indigo-500/[0.04] px-4 py-3">
              <p className="text-xs font-mono text-gray-300 truncate">{data.copilot_url}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={onCopyUrl}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-sm text-gray-200 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-green-400" />
                    <span className="text-green-400">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </>
                )}
              </button>
              <a
                href={data.copilot_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Open
              </a>
            </div>
          </div>
        </Panel>
      )}
    </div>
  );
}

function HealthPanel({ health }: { health: HealthMetrics | null }) {
  const rows = health
    ? [
        {
          label: 'Success rate',
          value: formatHealthMetric(health.success_rate, '%'),
          valueClass: 'text-green-400',
        },
        {
          label: 'Avg response',
          value: formatHealthMetric(health.avg_response_ms, 'ms'),
          valueClass: 'text-white',
        },
        {
          label: 'Error rate',
          value: formatHealthMetric(health.error_rate, '%'),
          valueClass: 'text-red-400',
        },
        {
          label: 'Active sessions',
          value: formatHealthMetric(health.active_sessions),
          valueClass: 'text-white',
        },
      ]
    : [
        { label: 'Success rate', value: 'N/A', valueClass: 'text-gray-500' },
        { label: 'Avg response', value: 'N/A', valueClass: 'text-gray-500' },
        { label: 'Error rate', value: 'N/A', valueClass: 'text-gray-500' },
        { label: 'Active sessions', value: 'N/A', valueClass: 'text-gray-500' },
      ];

  return (
    <Panel>
      <PanelHeader
        title="Health"
        subtitle={health ? 'Last 24 hours' : 'Metrics not available yet'}
      />
      <div className="p-5 space-y-3.5">
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between text-sm">
            <span className="text-gray-500">{row.label}</span>
            <span className={`font-medium ${row.valueClass}`}>{row.value}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function ResourcesTab({ knowledgeBases }: { knowledgeBases: ResourceMapKnowledgeBase[] }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-400">
        Shared knowledge sources at the copilot level.
      </p>
      <Panel>
        <PanelHeader title="Knowledge Bases" subtitle="Copilot-level knowledge sources" />
        {knowledgeBases.length === 0 ? (
          <p className="px-5 py-8 text-sm text-gray-500 text-center">
            No knowledge bases linked.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-800/80">
            {knowledgeBases.map((kb) => (
              <KnowledgeBaseRow key={kb.id} kb={kb} />
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function IntentsTab({ intents }: { intents: ResourceMapIntent[] }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-400">
        Each intent can link multiple resources. Access rules are scoped per intent when configured.
      </p>
      {intents.length === 0 ? (
        <Panel>
          <p className="px-5 py-8 text-sm text-gray-500 text-center">No intents configured.</p>
        </Panel>
      ) : (
        <div className="space-y-4">
          {intents.map((intent) => (
            <IntentCard key={intent.id} intent={intent} />
          ))}
        </div>
      )}
    </div>
  );
}

function AccessManagementTab({
  data,
  agentMasterId,
}: {
  data: EnterpriseChatBotResourceMap;
  agentMasterId: string;
}) {
  const { access_management: access } = data;

  const departments: AccessListItem[] = access.departments.map((d) => ({
    id: d.id,
    name: d.name,
    detail: `${d.member_count} members`,
  }));

  const users: AccessListItem[] = access.users.map((u) => ({
    id: u.id,
    name: u.name,
    detail: [u.email, u.department].filter(Boolean).join(' · '),
  }));

  const roles: AccessListItem[] = access.roles.map((r) => ({
    id: r.id,
    name: r.name,
    detail: `${r.user_count} users`,
  }));

  const exclusions: AccessListItem[] = access.exclusions.map((e) => ({
    id: e.id,
    name: e.name,
    detail: formatExclusionDetail(e),
  }));

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 px-5 py-4 flex items-start gap-3">
        <Shield className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-white">Copilot-level access</p>
          <p className="text-xs text-gray-400 mt-1 leading-relaxed">
            Broad permissions for the entire copilot. Intent-specific access is configured on each
            intent card.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AccessList
          icon={<Building2 className="w-4 h-4" />}
          title="Departments"
          items={departments}
          emptyText="No departments assigned."
        />
        <AccessList
          icon={<Users className="w-4 h-4" />}
          title="Users"
          items={users}
          emptyText="No users assigned."
        />
        <AccessList
          icon={<UserCheck className="w-4 h-4" />}
          title="Roles"
          items={roles}
          emptyText="No roles assigned."
        />
        <AccessList
          icon={<UserX className="w-4 h-4" />}
          title="Exclusions"
          items={exclusions}
          emptyText="No exclusions configured."
          variant="exclusion"
        />
      </div>

      <div className="flex justify-end">
        <NewTabLink
          href={`/portal/chatbot/${agentMasterId}`}
          className="inline-flex items-center gap-1.5 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
        >
          Manage access in copilot settings <ArrowUpRight className="w-4 h-4" />
        </NewTabLink>
      </div>
    </div>
  );
}

function formatExclusionDetail(exclusion: AccessExclusion): string | undefined {
  const parts = [exclusion.email, exclusion.note].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function ObservabilityTab({
  data,
}: {
  data: EnterpriseChatBotResourceMap;
}) {
  const health = data.health_metrics;
  const conversations = formatConversations24h(data.summary.conversations_24h);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard
          label="Conversations (24h)"
          value={conversations}
          icon={<MessageSquare className="w-4 h-4" />}
        />
        <StatCard
          label="Avg Response"
          value={formatHealthMetric(health?.avg_response_ms, 'ms')}
          icon={<Clock className="w-4 h-4" />}
        />
        <StatCard
          label="Success Rate"
          value={formatHealthMetric(health?.success_rate, '%')}
          icon={<TrendingUp className="w-4 h-4" />}
        />
        <StatCard
          label="Error Rate"
          value={formatHealthMetric(health?.error_rate, '%')}
          icon={<AlertTriangle className="w-4 h-4" />}
        />
        <StatCard
          label="Active Sessions"
          value={formatHealthMetric(health?.active_sessions)}
          icon={<Radio className="w-4 h-4" />}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel>
          <PanelHeader title="Top Intents" subtitle="By conversation volume (24h)" />
          <p className="px-5 py-8 text-sm text-gray-500 text-center">Not available yet.</p>
        </Panel>
        <Panel>
          <PanelHeader title="Recent Events" subtitle="Live activity log" />
          <p className="px-5 py-8 text-sm text-gray-500 text-center">Not available yet.</p>
        </Panel>
      </div>
    </div>
  );
}

function NotFoundState({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-32 text-center px-6">
      <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4">
        <AlertTriangle className="w-6 h-6 text-red-400" />
      </div>
      <h2 className="text-lg font-semibold text-white mb-2">Chatbot not found</h2>
      <p className="text-sm text-gray-400 max-w-md mb-6">
        The copilot you selected could not be loaded. It may have been removed or you may not have
        access.
      </p>
      <button
        type="button"
        onClick={onClose}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-sm text-gray-200 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to chat agents
      </button>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-32 text-center px-6">
      <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4">
        <AlertTriangle className="w-6 h-6 text-amber-400" />
      </div>
      <h2 className="text-lg font-semibold text-white mb-2">Failed to load copilot overview</h2>
      <p className="text-sm text-gray-400 max-w-md mb-6">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm transition-colors"
      >
        <RefreshCw className="w-4 h-4" />
        Retry
      </button>
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

const CopilotResourceMapModal: React.FC<CopilotResourceMapModalProps> = ({ agent, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [data, setData] = useState<EnterpriseChatBotResourceMap | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chatbotId = agent.agent_id || agent.id;

  const loadResourceMap = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    setError(null);

    try {
      const result = await fetchChatBotResourceMap(chatbotId);
      setData(result);
    } catch (err: unknown) {
      setData(null);
      const status = (err as Error & { status?: number }).status;
      if (status === 404) {
        setNotFound(true);
      } else {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  }, [chatbotId]);

  useEffect(() => {
    void loadResourceMap();
  }, [loadResourceMap]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleCopyUrl = async () => {
    if (!data?.copilot_url) return;
    try {
      await navigator.clipboard.writeText(data.copilot_url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const displayTitle = data?.agent_title ?? agent.agent_title;
  const displayId = data?.chatbot_id ?? agent.agent_id;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-neutral-950"
      role="dialog"
      aria-modal="true"
      aria-labelledby="copilot-overview-title"
    >
      <header className="shrink-0 border-b border-neutral-800 bg-neutral-900">
        <div className="flex items-center justify-between gap-4 px-5 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0 overflow-hidden">
              {agent.agent_icon ? (
                <img src={agent.agent_icon} alt="" className="w-full h-full object-cover" />
              ) : (
                <Network className="w-4 h-4 text-white" />
              )}
            </div>
            <div className="min-w-0">
              <h1 id="copilot-overview-title" className="text-sm font-semibold text-white truncate">
                {displayTitle}
              </h1>
              <p className="text-xs text-gray-500 font-mono truncate">{displayId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <NewTabLink
              href={`/portal/chatbot/${agent.id}`}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-indigo-400 hover:text-indigo-300 border border-neutral-800 hover:border-indigo-500/30 rounded-lg transition-colors"
            >
              Manage copilot <ArrowUpRight className="w-3 h-3" />
            </NewTabLink>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-neutral-800 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="flex gap-1 px-5 pb-0 overflow-x-auto" aria-label="Copilot overview tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              disabled={notFound}
              className={`flex items-center gap-2 px-3.5 py-2.5 text-sm rounded-t-lg border-b-2 transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-white bg-neutral-950/50'
                  : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="flex-1 overflow-y-auto bg-neutral-950">
        <div className="max-w-5xl mx-auto px-5 py-6">
          {notFound ? (
            <NotFoundState onClose={onClose} />
          ) : error ? (
            <ErrorState message={error} onRetry={() => void loadResourceMap()} />
          ) : (
            <>
              <SummaryCards data={data} loading={loading} />

              {loading ? (
                <LoadingSkeleton />
              ) : data ? (
                <>
                  {activeTab === 'overview' && (
                    <OverviewTab data={data} copied={copied} onCopyUrl={handleCopyUrl} />
                  )}
                  {activeTab === 'resources' && (
                    <ResourcesTab knowledgeBases={data.knowledge_bases} />
                  )}
                  {activeTab === 'intents' && <IntentsTab intents={data.intents} />}
                  {activeTab === 'access' && (
                    <AccessManagementTab data={data} agentMasterId={agent.id} />
                  )}
                  {activeTab === 'observability' && <ObservabilityTab data={data} />}
                </>
              ) : null}
            </>
          )}
        </div>
      </main>

      <footer className="shrink-0 border-t border-neutral-800 bg-neutral-900 px-5 py-2.5 flex items-center justify-between">
        <p className="text-xs text-gray-600 flex items-center gap-1.5">
          <Link2 className="w-3 h-3" />
          {loading ? 'Loading resource map…' : notFound ? 'Chatbot not found' : 'Live data'}
        </p>
        {data && !loading && (
          <p className="text-xs text-gray-600 hidden sm:block">
            {data.intents.length} intents · {data.access_management.departments.length} departments
            · {data.access_management.users.length} users
          </p>
        )}
      </footer>
    </div>
  );
};

export default CopilotResourceMapModal;
